import CryptoJS from 'crypto-js';

/**
 * Encryption Service using AES-256
 * All thoughts are encrypted client-side before storing in Firebase
 */

export class EncryptionService {
  /**
   * Encrypts text using AES encryption
   * @param text - Plain text to encrypt
   * @param passkey - User's passkey
   * @returns Encrypted text (base64) or empty string if input is empty
   */
  static encrypt(text: string, passkey: string): string {
    try {
      // Handle empty string - don't encrypt, just return empty
      if (text === '' || text === null || text === undefined) {
        return '';
      }
      const encrypted = CryptoJS.AES.encrypt(text, passkey).toString();
      return encrypted;
    } catch (error) {
      console.error('Encryption error:', error);
      throw new Error('Failed to encrypt data');
    }
  }

  /**
   * Decrypts text using AES decryption
   * @param encryptedText - Encrypted text (base64)
   * @param passkey - User's passkey
   * @returns Decrypted plain text or empty string if input is empty
   */
  static decrypt(encryptedText: string, passkey: string): string {
    try {
      // Handle empty string - return empty without trying to decrypt
      if (encryptedText === '' || encryptedText === null || encryptedText === undefined) {
        return '';
      }
      
      const decrypted = CryptoJS.AES.decrypt(encryptedText, passkey);
      const text = decrypted.toString(CryptoJS.enc.Utf8);
      
      // Empty result could be valid (empty content) or invalid passkey
      // We allow empty strings now
      return text;
    } catch (error) {
      console.error('Decryption error:', error);
      throw new Error('Failed to decrypt data. Invalid passkey?');
    }
  }

  /**
   * Generates a hash of the passkey for verification
   * @param passkey - User's passkey
   * @returns SHA-256 hash of the passkey
   */
  static hashPasskey(passkey: string): string {
    return CryptoJS.SHA256(passkey).toString();
  }

  /**
   * Verifies if a passkey is correct by comparing hashes
   * @param passkey - Passkey to verify
   * @param storedHash - Stored hash from Firestore
   * @returns True if passkey is correct
   */
  static verifyPasskey(passkey: string, storedHash: string): boolean {
    const inputHash = this.hashPasskey(passkey);
    return inputHash === storedHash;
  }

  /**
   * Generates a random salt for additional security
   * @returns Random salt string
   */
  static generateSalt(): string {
    return CryptoJS.lib.WordArray.random(128 / 8).toString();
  }

  /**
   * Derives a key from passkey and salt (PBKDF2)
   * @param passkey - User's passkey
   * @param salt - Salt string
   * @returns Derived key
   */
  static deriveKey(passkey: string, salt: string): string {
    return CryptoJS.PBKDF2(passkey, salt, {
      keySize: 256 / 32,
      iterations: 1000,
    }).toString();
  }
}

export default EncryptionService;

